import { useEffect, useState } from "react";
import { toast } from "sonner";
import { analyzeActiveChannel } from "@/lib/api";

/**
 * Claude-powered channel coaching report. Opens as a modal overlay.
 *
 * The analysis is cached server-side for 6h to avoid burning tokens
 * on repeat clicks — a "Re-analyze" link bypasses the cache. The
 * modal tells the user when they're looking at cached output.
 */
export default function AnalyzeModal({ channel, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forcing, setForcing] = useState(false);

  const load = async (force = false) => {
    if (force) setForcing(true);
    else setLoading(true);
    try {
      setReport(await analyzeActiveChannel(channel.channel_id, { force }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
      setReport({ failed: true });
    } finally {
      setLoading(false);
      setForcing(false);
    }
  };

  useEffect(() => { load(); }, [channel.channel_id]);

  return (
    <div
      data-testid="analyze-modal-overlay"
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        data-testid="analyze-modal"
        className="bg-zinc-950 border border-yellow-400/40 rounded-sm w-full max-w-3xl my-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-1">
              // CHANNEL ANALYSIS · CLAUDE SONNET 4.5
            </div>
            <h2 className="font-display text-2xl font-black text-white truncate">
              {channel.name}
            </h2>
          </div>
          <button
            data-testid="analyze-modal-close"
            onClick={onClose}
            className="font-mono text-zinc-400 hover:text-yellow-400 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading && (
            <div className="text-center py-10" data-testid="analyze-loading">
              <div className="font-mono text-sm text-yellow-400">
                [claude] auditing channel<span className="blink">_</span>
              </div>
              <div className="font-mono text-xs text-zinc-500 mt-2">
                Reviewing recent uploads, view patterns, and posting cadence
              </div>
            </div>
          )}

          {!loading && report?.failed && (
            <div
              className="border border-red-700/50 bg-red-950/30 rounded-sm p-5 text-red-200 text-sm"
              data-testid="analyze-error"
            >
              Couldn&apos;t generate analysis. Try again in a moment.
            </div>
          )}

          {!loading && report && !report.failed && (
            <>
              {report.summary && (
                <div
                  className="border-l-2 border-yellow-400 pl-4 py-1"
                  data-testid="analyze-summary"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 mb-1">
                    DIAGNOSIS
                  </div>
                  <div className="text-zinc-100 text-base leading-relaxed">
                    {report.summary}
                  </div>
                </div>
              )}

              <Section
                title="// STRENGTHS"
                items={report.strengths}
                accent="text-emerald-300"
                bullet="✓"
                testid="analyze-strengths"
              />
              <Section
                title="// WEAKNESSES"
                items={report.weaknesses}
                accent="text-amber-300"
                bullet="!"
                testid="analyze-weaknesses"
              />
              <Section
                title="// NEXT ACTIONS"
                items={report.next_actions}
                accent="text-yellow-200"
                bullet="▸"
                testid="analyze-next-actions"
                ordered
              />

              <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                  {report.cached
                    ? `cached ${new Date(report.cached_at).toLocaleString()}`
                    : "fresh analysis"}
                </span>
                <button
                  data-testid="analyze-reanalyze"
                  onClick={() => load(true)}
                  disabled={forcing}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 hover:text-yellow-300 disabled:text-zinc-500 transition-colors"
                >
                  {forcing ? "[claude] re-analyzing…" : "↻ Re-analyze (fresh)"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, items, accent, bullet, testid, ordered = false }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className={`font-mono text-[10px] uppercase tracking-[0.25em] ${accent} mb-2`}>
        {title}
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li
            key={`${testid}-${i}`}
            className="flex gap-3 text-zinc-100 text-sm leading-relaxed"
          >
            <span className={`font-mono ${accent} shrink-0 w-6 text-center`}>
              {ordered ? `${i + 1}` : bullet}
            </span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
